import { default } from './surveyItem.vue';
<script setup lang="ts">
  import { DisplayType, FormDesignItemDto, PropertyDisplayType } from '@/core/store/survey/surveyDesignDtos';
  import { SurveyResponseItemDto, SurveyResponseValueFile } from '@/core/store/survey/surveyResponseDtos';
  import Survey from '@/core/store/survey/surveyStore';
  import { computed, CSSProperties, PropType, Ref, ref, watch } from 'vue';

import { Sh } from '@/core/sharedService';
import { apiRoutes } from '@/core/apiRouters';
  const {
      displayMode, 
      isDesign,
      } = Survey();

   const props  = defineProps({
    item: {
      type: Object as () => FormDesignItemDto,
      required: true
    },
    response: {
      type: Object as () => SurveyResponseItemDto,
      default: null,
      required: false
    },
    onChange: {
      type: Function as PropType<( value: any) => void>,
      default: null,
      required: false,
    },
  });
  const fileRef: Ref<HTMLInputElement | null> = ref(null);
  const currentStyles = computed<PropertyDisplayType>(()=>{
    
      switch(displayMode.value){
        case DisplayType.DISPLAY: 
          return  {...props.item.properties.screen};
        case DisplayType.EDIT: 
          return  {...props.item.properties.input};
        case DisplayType.PDF: 
          return  {...props.item.properties.print};
      }
  });

  const cssInput = computed<CSSProperties>(() => {
    const displayProp: PropertyDisplayType=  currentStyles.value;
    return {
      backgroundColor:  displayProp.contentStyle.backgroundColor,
      borderRadius: '9999px',
      border: 'none',
      fontSize: Sh.getFontSize(displayProp.contentStyle.fontSize),
      color: displayProp.contentStyle.color,
      width: '100%'
    }
  });

  const cssGraphic = computed<CSSProperties>(() => {
    const displayProp: PropertyDisplayType = currentStyles.value;
    return {
      border: 'none',
      width: displayProp.contentStyle.width ? displayProp.contentStyle.width + "px" : "100px",
    }
  });

  const cssContainer = computed<CSSProperties>(() => {
    const displayProp: PropertyDisplayType = currentStyles.value;
    return {
      border: 'none',
      textAlign: displayProp.contentStyle.align
    }
  });

  const value = ref<SurveyResponseValueFile>(props.response  && props.response.value ? props.response.value : new SurveyResponseValueFile());
  
  watch(value, (newValue: SurveyResponseValueFile) => {
    if(props.response){
      if(JSON.stringify(newValue.toJSON()) !== JSON.stringify((<SurveyResponseValueFile>props.response.value).toJSON())){
        props.response.value = newValue;
        props.onChange(newValue);
      }
    }
  });

  const isCurrentVisible = computed(() => {
    const displayProp: PropertyDisplayType=  currentStyles.value;
    return displayProp.visible || isDesign;
  });

  const uploadFile = async (file: any) => {
    const formData = new FormData();
    formData.append("file", file)
    try{
      const result = await Sh.api<{id: string, url: string, originalName: string}>({
      method: 'POST',
      route: apiRoutes.files,
      data:formData, 
      type: "multipart"
    });
    const fileData = new SurveyResponseValueFile();
    fileData.id = result.id;
    fileData.originalName = result.originalName;
    fileData.url = result.url;
    value.value = fileData;
    }catch(e){
      console.log("ERror", e)
    }
  }

  const processFile = async (file: File) => {
    try{
      const formData = new FormData();
      formData.append("file", file);
      const result = await Sh.api<{id: string, url: string, originalName: string}>({
        method: "POST",
        route: apiRoutes.files,
        data:  formData,
        type: 'multipart'
      });
      const fileData = new SurveyResponseValueFile();
      fileData.id = result.id;
      fileData.originalName = result.originalName;
      fileData.url = result.url;
      value.value = fileData;
    }catch(e){
      console.log("ERror", e)
    }
  };

  const validateAndProcessFile = (file: File): boolean => {
    const acceptedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const maxSize = 10485760; // 10 MB EN BYTES
    if (!acceptedTypes.includes(file.type)) {
      Sh.showToast("Tipo de archivo no soportado. Solo se permiten jpg, png o gif", 10);
      return false;
    }
    if (file.size > maxSize) {
      Sh.showToast("Archivo demasiado grande, máximo 10Mb", 10);
      return false;
    }
    return true;
  };

  const handleDrop = async (event: DragEvent) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer?.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const file = droppedFiles[0];
      if(!validateAndProcessFile(file)) return;
      processFile(file);
    }
  };

  const loadFile = () => {
    fileRef.value?.click();
  }

  const changeFile = ($event: Event) => {
    const target = $event.target as HTMLInputElement;
    const file = target.files?.[0];
    
    if (props.item.format === 'image') {
      if(!validateAndProcessFile(file)) return;
      processFile(file);
    } else {
      if(file && file.size > 10485760){
        Sh.showToast("Archivo demasiado grande, máximo 10Mb", 10)
        return;
      }
      if(file){
        uploadFile(file);
      }
    }
  }

  const deleteFile = () => {
    value.value = new SurveyResponseValueFile();
  }
</script>

<template>
  <div v-if="displayMode == DisplayType.EDIT && isCurrentVisible ">
    <!-- Si es imagen, usar el comportamiento de arrastrar y soltar -->
    <div v-if="item.format === 'image'" 
         class="drowpdown-menu" 
         :style="{...cssContainer, backgroundColor: !isDesign && !value.url? 'lightgray' : 'transparent'}"
         @dragover.prevent
         @drop="handleDrop"
         @click="loadFile">
      <input type="file" 
             :hidden="true" 
             ref="fileRef" 
             @change="changeFile" 
             accept=".jpg,.jpeg,.png,.gif"
             :readonly="item.inheritItemId!=null && item.inheritItemId.trim().length!=0">
      <label v-if="!value.url && !isDesign">Arrastre un archivo (jpg, png o gif) o pulse aquí para buscarlo</label>
      <br  v-if="!value.url && !isDesign"/>
      <img :style="cssGraphic" :src="value.url && value.url.trim().length!=0 ? value.url : '/assets/image-placeholder.svg'">
    </div>

    <!-- Si no es imagen, mantener el comportamiento actual -->
    <div v-else>
      <input type="file" :hidden="true" ref="fileRef" @change="changeFile" :readonly="item.inheritItemId!=null && item.inheritItemId.trim().length!=0">
      <button class="btn-green-sm mR30px" v-if="!value.url" v-on:click="loadFile" :disabled="item.inheritItemId!=null && item.inheritItemId.trim().length!=0">Examinar...</button>
      <label v-if="!value.url"> Adjuntar fichero</label>
      <a target="_blank" v-if="value.url" :href="value.url">{{ value.originalName }}</a>
      <img v-if="value.url" class="button-component-icon" v-on:click="deleteFile" src="/assets/trash.svg"/>
    </div>
  </div>  

  <!-- Modificar la visualización en modo display para imágenes -->
  <div v-if="displayMode == DisplayType.DISPLAY && isCurrentVisible">
    <div v-if="item.format === 'image'" class="drowpdown-menu" :style="cssContainer">
      <img :style="cssGraphic" :src="value.url && value.url.trim().length!=0 ? value.url : '/assets/image-placeholder.svg'">
    </div>
    <a v-else target="_blank" :href="value.url">{{ isDesign ? "fichero subido.pdf" : value.originalName }}</a>
  </div>  

  <!-- Modificar la visualización en modo PDF para imágenes -->
  <div v-if="displayMode == DisplayType.PDF && isCurrentVisible">
    <div v-if="item.format === 'image'" class="drowpdown-menu" :style="cssContainer">
      <img :style="cssGraphic" :src="value.url && value.url.trim().length!=0 ? value.url : '/assets/image-placeholder.svg'">
    </div>
    <a v-else target="_blank" :href="value.url">{{ isDesign ? "fichero subido.pdf" : value.originalName }}</a>
  </div>  
</template>

<style lang="scss">
  @import url(../survey.scss);

  .dialogUpload{
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-content: space-around;
    align-items: center;
  }
  .dialogUpload label{
    margin: auto;
    text-align: center;
    padding: 20px;
  }

  .image-container {
    display: flex;
    flex-direction: column;
    align-items: inherit;
    width: 100%;
  }

  .design-image-placeholder {
    background-color: #e0e0e0;
    border: 1px dashed #999;
    min-height: 100px;
  }
</style>

<script lang="ts">
  export default {
    name: "SurveyItemFile"
  }
</script>